import React, { FC, useCallback, useEffect, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import useApi from '../../services/apiService';
import { PopupLocation } from './popupLocation';
import { useRouteService } from '../../helpers/customHooks';

type StyleProps = {
  borderBottomLeftRadius: number;
  borderBottomRightRadius: number;
  boxShadow: string;
  backdropFilter: string;
  border: string;
};

const useStyles = makeStyles<StyleProps>({ name: 'SearchInput' })(
  (theme, { borderBottomRightRadius, borderBottomLeftRadius, backdropFilter, boxShadow, border }) => ({
    root: {
      display: 'flex',
      alignItems: 'center',
      height: '54px',
      borderRadius: 0,
      borderTopLeftRadius: 35,
      borderBottomLeftRadius,
      borderTopRightRadius: 35,
      borderBottomRightRadius,
      background: 'rgba(255, 255, 255, 0.1)',
      boxShadow,
      backdropFilter,
      position: 'relative',
      ':after': {
        position: 'absolute',
        content: "''",
        bottom: '0px',
        left: 0,
        right: 0,
        margin: '0 16px',
        border,
      },
    },
    input: {
      flex: 1,
      paddingLeft: 45,
      color: 'white',
      fontSize: '0.875rem',
      '& .MuiInputBase-input:focus': {
        '&::placeholder': {
          opacity: 0,
        },
      },
    },
    iconButton: {
      padding: 10,
      marginRight: 15,
    },
    divider: {
      height: 28,
      margin: 4,
    },
    locationPaper: {
      height: '54px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    gridLocation: {
      minWidth: 72,
    },
    autocompleteGridContainer: {
      display: 'flex',
      flexWrap: 'nowrap',
    },
    searchIcon: {
      color: '#FFBF00',
    },
    btnLocation: {
      padding: 10,
      height: 47,
      width: 47,
    },
    popper: {
      inset: '0px auto auto 0px !important',
    },
    paper: {
      color: 'white',
      borderRadius: 0,
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
      borderBottomRightRadius: 35,
      borderBottomLeftRadius: 35,
      background: 'rgba(255, 255, 255, 0.1)',
      boxShadow: 'inset 0px -20px 19.4px rgb(194 194 194 / 10%), inset 0px 20px 0px rgb(255 255 255 / 0%)',
      backdropFilter: 'blur(19.4px)',
    },
    focused: {
      backgroundColor: 'inherit',
    },
    listbox: {
      padding: '8px 45px',
    },
    endAdornment: {
      display: 'none',
    },
    option: {
      paddingLeft: '0px !important',
      paddingRight: '0px !important',
      color: 'white',
      fontSize: '0.875rem',
    },
    noOptions: {
      padding: '14px 45px',
      color: 'white',
      fontSize: '0.875rem',
    },
    loading: {
      padding: '14px 45px',
      color: 'white',
      fontSize: '0.875rem',
    },
  })
);

export type searchType = {
  id: string;
  value: string;
};

export const SearchInput: FC<SearchInputType> = ({ city, setCity }) => {
  const routeSrv = useRouteService();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [options, setOptions] = useState<searchType[]>([]);
  const [loading, setLoading] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);

  const { eventsApi } = useApi();
  const history = useHistory();
  const { t } = useTranslation();

  const handleSearchIconClick = () => {
    setOpen(false);
  };

  const handleAutocompleteOnClick = (event, value) => {
    const eventShortInformation = value as searchType;
    const link = routeSrv.getEventRoute(eventShortInformation.id);
    history.push(link);
  };

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  useEffect(() => {
    if (search === '' || !search) {
      return undefined;
    }

    (async () => {
      setLoading(true);

      const events = await eventsApi.searchEvents(search);
      const opt = events.map((x) => ({
        id: x.slug,
        value: `${x.title}, ${x.city}`,
      }));
      setOptions(opt);

      setLoading(false);
    })();
  }, [search]);

  const handleClose = useCallback(() => {
    setOpenPopup(false);
  }, []);

  const styles = {
    borderBottomLeftRadius: open ? 0 : 35,
    borderBottomRightRadius: open ? 0 : 35,
    boxShadow: open
      ? 'inset 19.4px -19.4px 0px rgb(255 255 255 / 0%), inset 0px 20px 19.4px rgb(255 255 255 / 10%)'
      : 'inset 13.3px -13.3px 13.3px rgb(194 194 194 / 2%), inset -13.3px 13.3px 13.3px rgb(255 255 255 / 2%)',
    backdropFilter: open ? 'blur(19.4px)' : 'blur(1px)',
    border: open ? '0.5px solid #d8d8d84f' : 'none',
  };

  const { classes } = useStyles(styles);

  return (
    <Grid container direction="row" justifyContent="center" alignItems="center" spacing={0}>
      <PopupLocation setCity={setCity} open={openPopup} handleClose={handleClose} />
      <Grid item lg={8} xs={12} sm={8} md={8} xl={8}>
        <Autocomplete
          id="search"
          open={open}
          noOptionsText={t<string>('Dashboard.NothingFound')}
          loadingText={t<string>('Dashboard.Loading')}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          getOptionLabel={(option) => option.value}
          onChange={handleAutocompleteOnClick}
          options={options}
          loading={loading}
          classes={{
            popper: classes.popper,
            endAdornment: classes.endAdornment,
            paper: classes.paper,
            focused: classes.focused,
            listbox: classes.listbox,
            option: classes.option,
            noOptions: classes.noOptions,
            loading: classes.loading,
          }}
          renderInput={(params) => {
            const { InputLabelProps, InputProps, ...rest } = params;
            return (
              <div ref={params.InputProps.ref}>
                <Grid container className={classes.autocompleteGridContainer}>
                  <Grid item xs={12}>
                    <Paper className={classes.root}>
                      <InputBase
                        {...params.InputProps}
                        {...rest}
                        id={'searchInput'}
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        className={classes.input}
                        placeholder={t<string>('Search by Artist, Event or Venue ')}
                        sx={{
                          input: {
                            '&::placeholder': {
                              opacity: 1,
                            },
                          },
                        }}
                      />
                      <IconButton
                        className={classes.iconButton}
                        onClick={handleSearchIconClick}
                        disabled
                        size="large"
                      >
                        <SearchIcon className={classes.searchIcon} />
                      </IconButton>
                    </Paper>
                  </Grid>
                </Grid>
              </div>
            );
          }}
        />
      </Grid>
    </Grid>
  );
};

type SearchInputType = {
  city?: string;
  setCity?: (city: string) => void;
};
